import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

const ServicePageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  color: var(--text-light);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  margin-bottom: 80px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ContentSection = styled.div`
  h2 {
    font-size: 2rem;
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.6;
    margin-bottom: 20px;
  }

  ul {
    list-style: none;
    padding: 0;

    li {
      color: var(--text-light);
      padding: 10px 0;
      border-bottom: 1px solid #f0f0f0;

      &:before {
        content: "✓";
        color: var(--secondary-color);
        font-weight: bold;
        margin-right: 10px;
      }
    }
  }
`;

const ImageSection = styled.div`
  img {
    width: 100%;
    height: 400px;
    object-fit: cover;
    border-radius: 20px;
  }
`;

const OfficeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 30px;
  margin: 60px 0;
`;

const OfficeCard = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;

  h3 {
    font-size: 1.5rem;
    color: var(--text-dark);
    margin-bottom: 15px;
    font-family: "Phudu", sans-serif;
  }

  .features {
    list-style: none;
    padding: 0;
    margin-bottom: 20px;

    li {
      color: var(--text-light);
      padding: 5px 0;
      font-size: 0.9rem;

      &:before {
        content: "•";
        color: var(--secondary-color);
        margin-right: 10px;
      }
    }
  }

  .price {
    font-size: 1.25rem;
    color: var(--secondary-color);
    font-weight: bold;
    text-align: center;
    margin-top: 20px;
  }
`;

const CTASection = styled.div`
  background: var(--background-dark);
  color: white;
  padding: 60px 40px;
  border-radius: 20px;
  text-align: center;
  margin-bottom: 80px;

  h2 {
    font-size: 2rem;
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    margin-bottom: 30px;
    opacity: 0.9;
  }

  .btn {
    background: var(--secondary-color);
    color: white;
    padding: 15px 30px;
    border-radius: 10px;
    text-decoration: none;
    font-weight: 600;
    display: inline-block;
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 10px 20px rgba(245, 115, 58, 0.3);
    }
  }
`;

const MobileOffices = () => {
  return (
    <ServicePageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Mobile Offices</PageTitle>
          <PageSubtitle>
            Professional mobile office solutions perfect for construction sites,
            events, and temporary workspace needs. Fully equipped and ready to
            use.
          </PageSubtitle>
        </PageHeader>

        <ContentGrid>
          <ContentSection>
            <h2>Complete Mobile Office Solutions</h2>
            <p>
              Our mobile offices provide a professional workspace anywhere you
              need it. Perfect for construction sites, temporary projects,
              events, or when your main office is under renovation.
            </p>
            <p>
              Each mobile office is professionally converted from shipping
              containers, featuring all the amenities you need for productive
              work including climate control, electrical systems, and
              professional interior finishes.
            </p>
            <ul>
              <li>Professional interior with flooring and walls</li>
              <li>Climate control and insulation</li>
              <li>Electrical outlets and lighting</li>
              <li>Windows and secure entry door</li>
              <li>Desk space and storage options</li>
              <li>Internet and phone line ready</li>
            </ul>
          </ContentSection>

          <ImageSection>
            <img src="/assets/2025/08/Image5.jpg" alt="Mobile Office" />
          </ImageSection>
        </ContentGrid>

        <h2
          style={{
            textAlign: "center",
            marginBottom: "40px",
            fontFamily: "Phudu, sans-serif",
            color: "var(--text-dark)",
          }}
        >
          Mobile Office Options
        </h2>

        <OfficeGrid>
          <OfficeCard>
            <h3>Basic Mobile Office</h3>
            <ul className="features">
              <li>Single room layout (160 sq ft)</li>
              <li>Basic electrical and lighting</li>
              <li>Insulation and interior walls</li>
              <li>2 windows and entry door</li>
              <li>Basic flooring</li>
              <li>Heating and cooling unit</li>
            </ul>
            <div className="price">Starting at $15,000</div>
          </OfficeCard>

          <OfficeCard>
            <h3>Professional Office</h3>
            <ul className="features">
              <li>Multi-room layout with partition</li>
              <li>Professional interior finish</li>
              <li>Enhanced electrical system</li>
              <li>Multiple windows and entry</li>
              <li>Commercial-grade flooring</li>
              <li>Efficient HVAC system</li>
            </ul>
            <div className="price">Starting at $22,000</div>
          </OfficeCard>

          <OfficeCard>
            <h3>Executive Suite</h3>
            <ul className="features">
              <li>Premium multi-room design</li>
              <li>High-end interior finishes</li>
              <li>Complete electrical package</li>
              <li>Large windows and double doors</li>
              <li>Premium flooring and fixtures</li>
              <li>Advanced climate control</li>
            </ul>
            <div className="price">Starting at $35,000</div>
          </OfficeCard>

          <OfficeCard>
            <h3>Custom Configuration</h3>
            <ul className="features">
              <li>Tailored to your specifications</li>
              <li>Multiple container combinations</li>
              <li>Specialized room layouts</li>
              <li>Custom amenities and features</li>
              <li>Corporate branding options</li>
              <li>Advanced technology integration</li>
            </ul>
            <div className="price">Quote on Request</div>
          </OfficeCard>
        </OfficeGrid>

        <CTASection>
          <h2>Need a Mobile Office Solution?</h2>
          <p>
            Contact us to discuss your mobile office requirements. We offer
            purchase, lease, and rental options to fit your project timeline and
            budget.
          </p>
          <Link to="/contact" className="btn">
            Get Office Quote
          </Link>
        </CTASection>
      </Container>
    </ServicePageWrapper>
  );
};

export default MobileOffices;
